import { FC, useState, useMemo, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PortalHolding } from 'views/MyCellar/hooks/usePortalHoldings';
import { usePortfolioHoldingsTotals } from 'hooks/usePortfolioHoldingsTotals';
import { Product } from 'types/productType';
import { httpGet } from 'utils';
import { ChevronDown, ChevronUp } from '../../../../assets/icons';
import { useTheme } from '../../../../context/ThemeContext';
import { AppContext } from '../../../../context/ContextProvider';

// Theme colors matching other page components
const lightThemeColors = {
  background: '#FFFFFF',
  headerBg: '#1E293B',
  cardBackground: '#F8FAFC',
  text: '#FFFFFF',
  textDark: '#000000',
  textSecondary: '#64748B',
  headerTextSecondary: '#CBD5E1', // Brighter for dark header bar
  border: '#E2E8F0',
  positive: '#10B981',
  negative: '#EF4444',
  accent: '#14B8A6',
};

const darkThemeColors = {
  background: '#0F1E32',
  headerBg: '#0A1628',
  cardBackground: '#1A2E44',
  text: '#FFFFFF',
  textDark: '#FFFFFF',
  textSecondary: '#8B9CB3',
  headerTextSecondary: '#CBD5E1', // Brighter for dark header bar
  border: '#1E3A50',
  positive: '#10B981',
  negative: '#EF4444',
  accent: '#14B8A6',
};

interface SoldHoldingsResponse {
  data: Product[];
  size?: number;
}

const fetchSoldHoldings = (portfolioId?: number) =>
  httpGet<SoldHoldingsResponse, { portfolioId?: number }>('portal/SoldHoldings', portfolioId ? { portfolioId } : {});

interface AccountOverviewProps {
  holdings: PortalHolding[];
  isLoadingHoldings: boolean;
  cashBalance: number;
  balancePending: number;
  inflows: number;
  outflows: number;
  netPosition: number;
  netPositionPct: number;
  netCapitalInvested: number;
  portfolioValue: number;
  lastValuationDate?: string;
}

const formatCompact = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `£${(value / 1000000).toFixed(1)}m`;
  }
  if (absValue >= 1000) {
    return `£${value < 0 ? '-' : ''}${(Math.floor(absValue / 100) / 10).toFixed(1)}k`;
  }
  return `£${value.toFixed(0)}`;
};

const AccountOverview: FC<AccountOverviewProps> = ({
  holdings,
  isLoadingHoldings,
  cashBalance,
  balancePending,
  inflows,
  outflows,
  netPosition,
  netPositionPct,
  netCapitalInvested,
  lastValuationDate,
}) => {
  const { isDark } = useTheme();
  const themeColors = isDark ? darkThemeColors : lightThemeColors;
  const [isExpanded, setIsExpanded] = useState(true);
  const {
    state: {
      settings: { selectedPortfolioId },
    },
  } = useContext(AppContext);

  // Fetch sold holdings only when expanded (defer to avoid blocking initial render)
  const { data: soldHoldingsResponse } = useQuery({
    queryKey: ['accountOverviewSoldHoldings', selectedPortfolioId],
    queryFn: () => fetchSoldHoldings(selectedPortfolioId ?? undefined),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: isExpanded, // Only fetch when expanded section is visible
  });

  // Calculate current holdings stats
  const currentHoldingsStats = useMemo(() => {
    if (!holdings || holdings.length === 0) {
      return { aum: 0, wineCount: 0, openPnL: 0, openPnLPct: 0 };
    }

    const aum = holdings.reduce((sum, h) => sum + (h.totalValue || 0), 0);
    const totalCost = holdings.reduce((sum, h) => sum + (h.totalCost || 0), 0);
    const openPnL = holdings.reduce((sum, h) => sum + (h.pnl || 0), 0);
    const openPnLPct = totalCost > 0 ? (openPnL / totalCost) * 100 : 0;

    return { aum, wineCount: holdings.length, openPnL, openPnLPct };
  }, [holdings]);

  // Calculate realized trades stats
  const realizedTradesStats = useMemo(() => {
    const soldHoldings = soldHoldingsResponse?.data || [];
    if (soldHoldings.length === 0) {
      return { totalSales: 0, tradeCount: 0, realizedPnL: 0, winRate: 0 };
    }

    const totalSales = soldHoldings.reduce((sum, h) => sum + (h.totalValue || 0), 0);
    const realizedPnL = soldHoldings.reduce((sum, h) => sum + (h.profitAndLoss || 0), 0);
    const wins = soldHoldings.filter((h) => (h.profitAndLoss || 0) > 0).length;
    const winRate = soldHoldings.length > 0 ? (wins / soldHoldings.length) * 100 : 0;

    return { totalSales, tradeCount: soldHoldings.length, realizedPnL, winRate };
  }, [soldHoldingsResponse]);

  // Calculate net position stats
  const netPositionStats = useMemo(() => {
    return { totalIn: inflows, totalOut: outflows, netCapital: netCapitalInvested, netPosition };
  }, [inflows, outflows, netCapitalInvested, netPosition]);

  const { grandTotal } = usePortfolioHoldingsTotals();
  const nav = grandTotal + cashBalance + balancePending;
  const calculatedNetPosition = nav - netCapitalInvested;

  // Only block initial render on holdings (not sold holdings — that loads when expanded)
  if (isLoadingHoldings && holdings.length === 0) {
    return (
      <div className="rounded-lg overflow-hidden animate-pulse" style={{ backgroundColor: themeColors.headerBg }}>
        <div className="h-12 flex items-center px-5">
          <div className="h-4 w-32 rounded" style={{ backgroundColor: themeColors.cardBackground }} />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden shadow-md" style={{ backgroundColor: themeColors.headerBg }}>
      {/* Header Bar */}
      <div
        className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 sm:gap-x-6 min-w-0">
          {/* NAV */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <svg
              className="w-5 h-5 hidden sm:block flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke={themeColors.text}
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="text-xs sm:text-sm whitespace-nowrap" style={{ color: themeColors.headerTextSecondary }}>
              NAV:
            </span>
            <span className="text-sm sm:text-base font-semibold whitespace-nowrap" style={{ color: themeColors.text }}>
              {formatCompact(nav)}
            </span>
          </div>

          {/* Net Position */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <svg
              className="w-5 h-5 hidden sm:block flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke={themeColors.text}
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <span className="text-xs sm:text-sm whitespace-nowrap" style={{ color: themeColors.headerTextSecondary }}>
              Net Position:
            </span>
            <span
              className="text-sm sm:text-base font-semibold whitespace-nowrap"
              style={{ color: calculatedNetPosition >= 0 ? themeColors.positive : themeColors.negative }}
            >
              {formatCompact(calculatedNetPosition)}
            </span>
            <span
              className="text-xs sm:text-sm whitespace-nowrap"
              style={{ color: calculatedNetPosition >= 0 ? themeColors.positive : themeColors.negative }}
            >
              ({netPositionPct.toFixed(1)}%)
            </span>
          </div>

          {/* Cash */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <svg
              className="w-5 h-5 hidden sm:block flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke={themeColors.text}
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-xs sm:text-sm whitespace-nowrap" style={{ color: themeColors.headerTextSecondary }}>
              Balance (incl pending):
            </span>
            <span className="text-sm sm:text-base font-semibold whitespace-nowrap" style={{ color: themeColors.text }}>
              {formatCompact(cashBalance + balancePending)}
            </span>
          </div>
        </div>

        {/* Valuation Date + Toggle Button */}
        <div className="flex items-center gap-4 flex-shrink-0">
          {lastValuationDate && (
            <span
              className="text-[10px] sm:text-xs whitespace-nowrap"
              style={{ color: themeColors.headerTextSecondary }}
            >
              As at{' '}
              {new Date(lastValuationDate).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm whitespace-nowrap" style={{ color: themeColors.headerTextSecondary }}>
              Account Overview
            </span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" style={{ color: themeColors.text }} />
            ) : (
              <ChevronDown className="w-4 h-4" style={{ color: themeColors.text }} />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 px-5 py-4 border-t"
          style={{ backgroundColor: isDark ? themeColors.background : '#F8FAFC', borderColor: themeColors.border }}
        >
          {/* Current Holdings */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-5 rounded-full" style={{ backgroundColor: themeColors.accent }} />
              <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: themeColors.textDark }}>
                Current Holdings
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                  AUM:
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-semibold" style={{ color: themeColors.textDark }}>
                    {formatCompact(currentHoldingsStats.aum)}
                  </span>
                  <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                    {currentHoldingsStats.wineCount} wines
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                  Open P&L:
                </span>
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-base font-semibold flex items-center gap-1"
                    style={{ color: currentHoldingsStats.openPnL >= 0 ? themeColors.positive : themeColors.negative }}
                  >
                    {currentHoldingsStats.openPnL >= 0 ? '↗' : '↘'} {formatCompact(currentHoldingsStats.openPnL)}
                  </span>
                  <span
                    className="text-sm"
                    style={{ color: currentHoldingsStats.openPnL >= 0 ? themeColors.positive : themeColors.negative }}
                  >
                    {currentHoldingsStats.openPnLPct.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Realized Trades */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-5 rounded-full" style={{ backgroundColor: themeColors.accent }} />
              <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: themeColors.textDark }}>
                Realized Trades
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                  Total Sales:
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-semibold" style={{ color: themeColors.textDark }}>
                    {formatCompact(realizedTradesStats.totalSales)}
                  </span>
                  <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                    {realizedTradesStats.tradeCount} trades
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                  Realized P&L:
                </span>
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-base font-semibold flex items-center gap-1"
                    style={{
                      color: realizedTradesStats.realizedPnL >= 0 ? themeColors.positive : themeColors.negative,
                    }}
                  >
                    {realizedTradesStats.realizedPnL >= 0 ? '↗' : '↘'} {realizedTradesStats.realizedPnL >= 0 ? '+' : ''}
                    {formatCompact(realizedTradesStats.realizedPnL)}
                  </span>
                  <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                    Win rate: {realizedTradesStats.winRate.toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Net Position */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-5 rounded-full" style={{ backgroundColor: themeColors.positive }} />
              <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: themeColors.textDark }}>
                Net Position
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                  Total In:
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-semibold" style={{ color: themeColors.textDark }}>
                    {formatCompact(netPositionStats.totalIn)}
                  </span>
                  <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                    Out: {formatCompact(netPositionStats.totalOut)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                  Net Capital:
                </span>
                <span className="text-base font-semibold" style={{ color: themeColors.textDark }}>
                  {formatCompact(netPositionStats.netCapital)}
                </span>
              </div>
              <div
                className="flex justify-between items-baseline rounded-md px-2 py-1.5 -mx-2"
                style={{ backgroundColor: isDark ? themeColors.cardBackground : '#F1F5F9' }}
              >
                <span className="text-sm" style={{ color: themeColors.textSecondary }}>
                  Net Position:
                </span>
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-base font-semibold flex items-center gap-1"
                    style={{ color: calculatedNetPosition >= 0 ? themeColors.positive : themeColors.negative }}
                  >
                    {calculatedNetPosition >= 0 ? '↗' : '↘'} {formatCompact(calculatedNetPosition)}
                  </span>
                  <span
                    className="text-sm"
                    style={{ color: calculatedNetPosition >= 0 ? themeColors.positive : themeColors.negative }}
                  >
                    {netPositionPct.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountOverview;
